/* ---------------------------------------------------------------------------
   Printing and exporting the documents drawn in the record's content panel —
   the inventory sheet, the network summary and the period report.

   PRINT AND PDF ARE THE PRINT STYLESHEET, both of them. Every browser this
   application supports saves a PDF from its print dialog, and the print rules
   in src/styles/app.css are already what lays these documents out on paper; a
   second PDF renderer in the bundle would be a second layout to keep agreeing
   with the first. "PDF" names the file after the document before opening the
   dialog, which is where the browser takes the file name from.

   WORD IS A .docx BUILT HERE, with no library: one `altChunk` in an otherwise
   empty document, holding the document's HTML as MHT with its images inside.
   Word converts the chunk to its own paragraphs and tables when it opens the
   file. What goes into the chunk is what the print stylesheet would put on
   paper — everything it hides is dropped — with the controls turned into the
   text they show and a plain stylesheet Word can read in place of the app's.
   A chart, which Word will not draw from HTML, goes in as its picture: the
   SVG with the colours the stylesheet gave it, drawn to a PNG (LC-55).
   --------------------------------------------------------------------------- */

/** Print the page. The print stylesheet decides what goes on paper. */
export function printDocument(): void {
  window.print();
}

/**
 * Save as PDF through the print dialog, with the document's name as the file name.
 * The title is put back once the dialog closes.
 */
export function savePdf(name: string): void {
  const before = document.title;
  document.title = fileSafe(name);
  const restore = () => {
    document.title = before;
    window.removeEventListener('afterprint', restore);
  };
  window.addEventListener('afterprint', restore);
  window.print();
}

/** Save `root` — the document on screen — as a Word document named `name`. */
export async function saveWord(root: HTMLElement, name: string): Promise<void> {
  const charts = await chartPictures(root);
  /* INTO A DOCUMENT WITH NO BROWSING CONTEXT (QA 2026-09-30). The images are
     re-pointed at the MHT's own `file:///C:/doc/…` names, and an <img> owned
     by the page's document fetches whatever its src says — so the browser asked
     the disk for image0.jpeg on every export. An inert document loads nothing. */
  const inert = document.implementation.createHTMLDocument('');
  const clone = inert.importNode(printableClone(root, charts), true) as HTMLElement;
  inert.body.appendChild(clone);
  const images = await embedImages(clone);
  const html =
    '<!DOCTYPE html><html><head><meta charset="utf-8">' +
    `<title>${escapeXml(name)}</title><style>${WORD_CSS}</style></head>` +
    `<body>${clone.innerHTML}</body></html>`;
  const docx = buildDocx(mht(html, images));
  download(new Blob([docx], { type: DOCX_TYPE }), `${fileSafe(name)}.docx`);
}

/* --------------------------- what goes into the document --------------------------- */

/** Always dropped: controls, icons and scripts have nothing to say on paper. */
const ALWAYS_DROPPED = 'button, script, style, svg, canvas, iframe, noscript, [aria-hidden="true"], [data-keep]';

/**
 * A copy of `root` as the print stylesheet would lay it out: every element a
 * print rule hides is left out, and a select or an input becomes its value.
 */
function printableClone(root: HTMLElement, charts: ReadonlyMap<string, string> = new Map()): HTMLElement {
  const MARK = 'data-xp-drop';
  const marked: Element[] = [];
  for (const sel of [ALWAYS_DROPPED, ...printHiddenSelectors()]) {
    let found: NodeListOf<Element>;
    try {
      found = root.querySelectorAll(sel);
    } catch {
      continue;
    }
    for (const el of found) {
      el.setAttribute(MARK, '');
      marked.push(el);
    }
  }

  /* Values are read off the originals: a clone carries a select's markup, not
     which option somebody picked. */
  const values = new Map<Element, string>();
  for (const el of root.querySelectorAll('select, input, textarea')) {
    if (el instanceof HTMLSelectElement) values.set(el, el.selectedOptions[0]?.text ?? '');
    else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) values.set(el, el.value);
  }
  const order = [...values.keys()];
  order.forEach((el, i) => el.setAttribute('data-xp-val', String(i)));

  const clone = root.cloneNode(true) as HTMLElement;
  for (const el of marked) el.removeAttribute(MARK);
  for (const el of order) el.removeAttribute('data-xp-val');

  /* A chart goes in as its picture, before the SVG it was drawn from is dropped. */
  for (const el of clone.querySelectorAll('[data-xp-chart]')) {
    const src = charts.get(el.getAttribute('data-xp-chart') ?? '');
    if (!src) continue;
    const img = document.createElement('img');
    img.setAttribute('src', src);
    img.setAttribute('alt', el.getAttribute('aria-label') ?? 'Chart');
    el.replaceWith(img);
  }
  for (const el of root.querySelectorAll('[data-xp-chart]')) el.removeAttribute('data-xp-chart');
  for (const el of clone.querySelectorAll(`[${MARK}]`)) el.remove();
  for (const el of clone.querySelectorAll('[data-xp-val]')) {
    const text = values.get(order[Number(el.getAttribute('data-xp-val'))]!) ?? '';
    el.replaceWith(document.createTextNode(text));
  }
  return clone;
}

/** The selectors the print stylesheet sets to `display: none`. */
function printHiddenSelectors(): string[] {
  const out: string[] = [];
  const walk = (rules: CSSRuleList, inPrint: boolean) => {
    for (const rule of rules) {
      if (rule instanceof CSSMediaRule) {
        walk(rule.cssRules, inPrint || /\bprint\b/.test(rule.media.mediaText));
      } else if (inPrint && rule instanceof CSSStyleRule && rule.style.display === 'none') {
        out.push(rule.selectorText);
      }
    }
  };
  for (const sheet of document.styleSheets) {
    try {
      walk(sheet.cssRules, false);
    } catch {
      /* A stylesheet from another origin cannot be read, and none of ours is one. */
    }
  }
  return out;
}

/* --------------------------- the charts --------------------------- */

/** The paint properties a chart takes from the stylesheet, copied onto each mark. */
const CHART_PAINT = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-opacity',
  'opacity',
  'font-size',
  'font-family',
  'font-weight',
  'visibility',
] as const;

/**
 * One chart as a standalone SVG document: its marks carrying the colours the
 * stylesheet gave them on screen, so it draws the same with no stylesheet at
 * all (LC-55). Pure over the element it is given, and read by `npm test`.
 */
export function standaloneSvg(svg: SVGElement): string {
  const view = svg.ownerDocument.defaultView ?? window;
  const copy = svg.cloneNode(true) as SVGElement;
  const from = [svg, ...svg.querySelectorAll('*')];
  const to = [copy, ...copy.querySelectorAll('*')];
  from.forEach((el, i) => {
    const style = view.getComputedStyle(el as Element);
    const paint = CHART_PAINT.map((p) => [p, style.getPropertyValue(p)] as const).filter(([, v]) => v !== '');
    if (paint.length) (to[i] as Element).setAttribute('style', paint.map(([p, v]) => `${p}:${v}`).join(';'));
  });
  const box = (svg.getAttribute('viewBox') ?? '0 0 960 260').split(/\s+/).map(Number);
  copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  copy.setAttribute('width', String(box[2] ?? 960));
  copy.setAttribute('height', String(box[3] ?? 260));
  copy.removeAttribute('class');
  return new view.XMLSerializer().serializeToString(copy);
}

/**
 * Every chart in the document as a picture Word can place, keyed by the mark
 * left on it: a PNG where the browser can draw one, the SVG itself where it
 * cannot. Word takes no SVG from HTML, and a report whose S-curve vanished on
 * the way into Word would be the one document it was needed in.
 */
async function chartPictures(root: HTMLElement): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  const charts = [...root.querySelectorAll<SVGElement>('svg.pchart__svg')];
  await Promise.all(
    charts.map(async (svg, i) => {
      const key = String(i);
      svg.setAttribute('data-xp-chart', key);
      const markup = standaloneSvg(svg);
      const svgUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(markup)))}`;
      try {
        const img = new Image();
        img.src = svgUrl;
        await img.decode();
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 960;
        canvas.height = img.naturalHeight || 260;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('no canvas');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        out.set(key, canvas.toDataURL('image/png'));
      } catch {
        out.set(key, svgUrl);
      }
    }),
  );
  return out;
}

interface Embedded {
  location: string;
  type: string;
  base64: string;
}

/**
 * Put the document's images inside the file, since Word cannot fetch them from
 * a register that asks who is asking. An image that will not load is left out
 * rather than left as a broken link.
 */
async function embedImages(root: HTMLElement): Promise<Embedded[]> {
  const out: Embedded[] = [];
  await Promise.all(
    [...root.querySelectorAll('img')].map(async (img, i) => {
      const src = img.getAttribute('src');
      if (!src) return void img.remove();
      try {
        const res = await fetch(new URL(src, location.href), { credentials: 'include' });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (!blob.type.startsWith('image/')) throw new Error(blob.type);
        const ext = blob.type.split('/')[1]!.replace(/[^a-z0-9]/g, '') || 'img';
        const location_ = `file:///C:/doc/image${i}.${ext}`;
        out.push({ location: location_, type: blob.type, base64: await toBase64(blob) });
        img.setAttribute('src', location_);
        img.removeAttribute('loading');
        img.setAttribute('style', 'max-width:16cm');
      } catch {
        img.remove();
      }
    }),
  );
  return out;
}

/** Plain rules Word's HTML import understands, in place of the app's own. */
const WORD_CSS = `
body { font-family: Calibri, Arial, sans-serif; font-size: 10pt; color: #1a1a1a; }
h1 { font-size: 18pt; margin: 0 0 6pt; }
h2 { font-size: 14pt; margin: 12pt 0 6pt; }
h3, h4 { font-size: 11pt; margin: 10pt 0 4pt; }
p { margin: 0 0 6pt; }
table { border-collapse: collapse; width: 100%; margin: 4pt 0 10pt; }
th, td { border: 1px solid #bfbfbf; padding: 3pt 5pt; vertical-align: top; font-size: 9pt; }
th { background: #f2f2f2; text-align: left; font-weight: bold; }
img { max-width: 16cm; }
`;

/* --------------------------- the file --------------------------- */

const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** The HTML and its images as one MHT part, which is what the altChunk carries. */
function mht(html: string, images: readonly Embedded[]): string {
  const B = '----=bridgeWatchPart';
  const parts = [
    `--${B}\r\nContent-Type: text/html; charset="utf-8"\r\nContent-Transfer-Encoding: quoted-printable\r\n` +
      `Content-Location: file:///C:/doc/document.html\r\n\r\n${quotedPrintable(html)}\r\n`,
    ...images.map(
      (img) =>
        `--${B}\r\nContent-Type: ${img.type}\r\nContent-Transfer-Encoding: base64\r\n` +
        `Content-Location: ${img.location}\r\n\r\n${img.base64.replace(/.{76}/g, '$&\r\n')}\r\n`,
    ),
  ];
  return (
    `MIME-Version: 1.0\r\nContent-Type: multipart/related; type="text/html"; boundary="${B}"\r\n\r\n` +
    `${parts.join('')}--${B}--\r\n`
  );
}

/** Quoted-printable over UTF-8 bytes, with soft breaks under 76 characters. */
function quotedPrintable(text: string): string {
  const bytes = new TextEncoder().encode(text.replace(/\r?\n/g, '\n'));
  let out = '';
  let line = 0;
  for (const b of bytes) {
    if (b === 10) {
      out += '\r\n';
      line = 0;
      continue;
    }
    const plain = b >= 33 && b <= 126 && b !== 61;
    const piece = plain || b === 32 ? String.fromCharCode(b) : `=${b.toString(16).toUpperCase().padStart(2, '0')}`;
    if (line + piece.length > 75) {
      out += '=\r\n';
      line = 0;
    }
    out += piece;
    line += piece.length;
  }
  return out;
}

function buildDocx(chunk: string): Uint8Array {
  const xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
  return zip([
    [
      '[Content_Types].xml',
      `${xml}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Default Extension="mht" ContentType="message/rfc822"/>' +
        '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>' +
        '</Types>',
    ],
    [
      '_rels/.rels',
      `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>' +
        '</Relationships>',
    ],
    [
      'word/_rels/document.xml.rels',
      `${xml}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        '<Relationship Id="htmlChunk" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/aFChunk" Target="afchunk.mht"/>' +
        '</Relationships>',
    ],
    [
      'word/document.xml',
      `${xml}<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" ` +
        'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><w:body>' +
        '<w:altChunk r:id="htmlChunk"/>' +
        /* A4, 2 cm margins. */
        '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/>' +
        '<w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134" w:header="708" w:footer="708" w:gutter="0"/>' +
        '</w:sectPr></w:body></w:document>',
    ],
    ['word/afchunk.mht', chunk],
  ]);
}

/* --------------------------- a stored zip --------------------------- */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const b of bytes) c = CRC_TABLE[(c ^ b) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Files, uncompressed. A .docx needs to be a zip, not a small one. */
function zip(files: readonly (readonly [string, string])[]): Uint8Array {
  const enc = new TextEncoder();
  const local: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const [path, text] of files) {
    const name = enc.encode(path);
    const data = enc.encode(text);
    const crc = crc32(data);

    const head = new DataView(new ArrayBuffer(30));
    head.setUint32(0, 0x04034b50, true);
    head.setUint16(4, 20, true);
    head.setUint16(6, 0x0800, true); // UTF-8 names
    head.setUint16(8, 0, true); // stored
    head.setUint32(14, crc, true);
    head.setUint32(18, data.length, true);
    head.setUint32(22, data.length, true);
    head.setUint16(26, name.length, true);
    local.push(new Uint8Array(head.buffer), name, data);

    const dir = new DataView(new ArrayBuffer(46));
    dir.setUint32(0, 0x02014b50, true);
    dir.setUint16(4, 20, true);
    dir.setUint16(6, 20, true);
    dir.setUint16(8, 0x0800, true);
    dir.setUint32(16, crc, true);
    dir.setUint32(20, data.length, true);
    dir.setUint32(24, data.length, true);
    dir.setUint16(28, name.length, true);
    dir.setUint32(42, offset, true);
    central.push(new Uint8Array(dir.buffer), name);

    offset += 30 + name.length + data.length;
  }

  const dirSize = central.reduce((n, a) => n + a.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, dirSize, true);
  end.setUint32(16, offset, true);

  const all = [...local, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(all.reduce((n, a) => n + a.length, 0));
  let at = 0;
  for (const a of all) {
    out.set(a, at);
    at += a.length;
  }
  return out;
}

/* --------------------------- helpers --------------------------- */

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function fileSafe(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

function escapeXml(text: string): string {
  return text.replace(/[<>&"]/g, (c) => `&${{ '<': 'lt', '>': 'gt', '&': 'amp', '"': 'quot' }[c]};`);
}
